import { ChevronLeftIcon } from '@chakra-ui/icons'
import { Box, Flex, IconButton, useDisclosure } from '@chakra-ui/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  getProfileLayoutMode,
  ProfileLayoutContext,
  ProfileLayoutData,
} from '../../../../../contexts/user/ProfileLayoutContext'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useResizeObserver } from '../../../../../hooks/useResizeObserver'
import { setLocalStorage } from '../../../../../utils/localStorage'
import { BodyContent } from './Nav/BodyContent'
import { BodyHeader } from './Nav/BodyHeader'
import { PatientHeaderBar } from './Patient/PatientHeaderBar'
import { PatientInfo } from './Patient/PatientInfo'
import { PatientInfoDrawer } from './Patient/PatientInfoDrawer'

const SIDEBAR_PINNED_KEY = 'v2-profile-sidebar-pinned'

export const ProfileBody = ({ height, width }: { width: number; height: number }) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const bodySize = useResizeObserver(bodyRef, 'content')

  const layoutMode = getProfileLayoutMode(width)
  const storedPinned = useLocalStorage<boolean>(SIDEBAR_PINNED_KEY)
  const [pinned, setPinnedState] = useState(storedPinned ?? width >= 900)
  const setPinned = useCallback((p: boolean) => {
    setPinnedState(p)
    setLocalStorage(SIDEBAR_PINNED_KEY, p)
  }, [])
  const { isOpen, onOpen, onClose } = useDisclosure()

  const canPin = layoutMode === 'tablet'
  const sidebarInFlow = layoutMode === 'desktop' || (canPin && pinned)

  const [focusMode, setFocusMode] = useState(false)

  const layout = useMemo<ProfileLayoutData>(
    () => ({
      layoutMode,
      width,
      height,
      focusMode,
      setFocusMode,
      sidebar: { isOpen, onOpen, onClose, canPin, isPinned: pinned, setPinned },
    }),
    [layoutMode, width, height, focusMode, isOpen, onOpen, onClose, canPin, pinned, setPinned],
  )

  return (
    <ProfileLayoutContext.Provider value={layout}>
      <Flex height={`${height}px`} width={`${width}px`}>
        {sidebarInFlow ? (
          <Flex position="relative" borderRight="1px solid #cdcdcd" w="290px" h="100%">
            <PatientInfo />
            {canPin ? (
              <IconButton
                aria-label="Collapse patient panel"
                position="absolute"
                right="-12px"
                top="60px"
                zIndex={2}
                size="xs"
                borderRadius="full"
                bg="white"
                border="1px solid #cdcdcd"
                _hover={{ bg: 'gray.100' }}
                icon={<ChevronLeftIcon w={4} h={4} color="gray.600" />}
                onClick={() => setPinned(false)}
              />
            ) : null}
          </Flex>
        ) : null}
        <Flex h="100%" flex={1} minW="0">
          <Flex w="100%" h="100%" flexFlow="column">
            {sidebarInFlow ? null : <PatientHeaderBar />}
            <BodyHeader />
            <Box ref={bodyRef} w="100%" flex={1} minH={0}>
              <BodyContent size={bodySize} />
            </Box>
          </Flex>
        </Flex>
        {sidebarInFlow ? null : <PatientInfoDrawer />}
      </Flex>
    </ProfileLayoutContext.Provider>
  )
}
